import { useActiveTeamId } from '@common/teams/hooks';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useUser } from 'frontend/core/src/context/user';
import { useRestQuery } from 'frontend/core/src/data/util';
import { useState, useEffect, useMemo } from 'react';

import { DEFAULT_CUSTOMER_BALANCE, DEFAULT_PROFESSIONAL_METERING_INFO } from './constants';
import { getCustomerBalance, getProfessionalMeteringInfo } from './data/api';
import { computeError, ProfessionalEditionCreditsContext } from './ProfessionalEditionCreditsProvider';
import { CustomerBalance, ProfessionalMeteringInfo } from './types';
import { UsageAlertDialog } from './UsageAlertDialog';

const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

export const ProfessionalEditionCreditsProvider = ({
  children,
  teamId
}: {
  children: React.ReactNode;
  teamId?: string;
}) => {
  const { activeTeamId, setActiveTeamId } = useActiveTeamId();
  const { user } = useUser();

  const teamIdToUse = teamId || activeTeamId;
  const { isProfessionalEditionEnabled } = useGlobalVariables();
  const [meteringInfo, setMeteringInfo] = useState<ProfessionalMeteringInfo>(DEFAULT_PROFESSIONAL_METERING_INFO);
  const [customerBalance, setCustomerBalance] = useState<CustomerBalance>(DEFAULT_CUSTOMER_BALANCE);

  const {
    data,
    isLoading: meteringInfoLoading,
    refetch,
    error
  } = useRestQuery<ProfessionalMeteringInfo>(
    ['get-credit-usage', teamIdToUse],
    () => getProfessionalMeteringInfo(teamIdToUse),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isProfessionalEditionEnabled && !!teamIdToUse,
      onError: () => {
        return;
      },
      refetchInterval: REFRESH_INTERVAL
    }
  );

  const { data: customerBalanceData, isLoading: customerBalanceLoading } = useRestQuery<CustomerBalance>(
    ['get-customer-balance', user.id, teamIdToUse],
    () => getCustomerBalance(user.id, teamIdToUse),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isProfessionalEditionEnabled && !!teamIdToUse && !!user.id,
      onError: () => {
        return;
      }
    }
  );

  useEffect(() => {
    if (data) {
      setMeteringInfo(data?.data as ProfessionalMeteringInfo);
    }
  }, [data]);

  useEffect(() => {
    if (customerBalanceData) {
      setCustomerBalance(customerBalanceData?.data as CustomerBalance);
    }
  }, [customerBalanceData]);

  const updateMeteringInfo = usePersistentCallback((updates: Partial<ProfessionalMeteringInfo>) => {
    setMeteringInfo((prev) => ({ ...prev, ...updates }));
  });

  const customError = computeError(error);

  const value = useMemo(
    () => ({
      meteringInfo,
      isLoading: meteringInfoLoading || customerBalanceLoading,
      refetch,
      setActiveTeamId,
      activeTeamId,
      error: customError,
      updateMeteringInfo,
      customerBalance,
      customerBalanceLoading
    }),
    [
      meteringInfo,
      meteringInfoLoading,
      customerBalanceLoading,
      refetch,
      setActiveTeamId,
      activeTeamId,
      customError,
      updateMeteringInfo,
      customerBalance
    ]
  );
  return (
    <ProfessionalEditionCreditsContext.Provider value={value}>
      {children} <UsageAlertDialog />
    </ProfessionalEditionCreditsContext.Provider>
  );
};
